import { observable, action, runInAction, computed } from "mobx";
import moment from "moment";
import debounce from "lodash/debounce";

import { MultiSearchFormStore } from "./MultiSearchFormStore";
import EnvironmentUtils from "src/utils/EnvironmentUtils.js";

export const MULTISEARCH_USAGE = {
  ADVANCED_SEARCH: "ADVANCED_SEARCH",
  NAVIGATOR_COMPONENT: "NAVIGATOR_COMPONENT",
};

export default class MultiSearchStore {
  @observable form;
  @observable loadingQuery = false;

  // Maintenance Agency
  @observable maintenanceAgencies = {};
  @observable selectedMaintenanceAgency = [];

  // Micro Cubes
  @observable showMicroCubes = true;

  // Results
  @observable.shallow cubesResults = observable.array();
  @observable.shallow combinationsResults = observable.array();

  @observable combinedResults = {
    CUBES: [],
    COMBINATIONS: [],
    combinedAllowedFilters: observable.array(),
  };

  @observable similarityThresholdPercentage =
    this.getDefaultSimilarityThresholdPercentage();
  typesConfiguration = {
    FETCH_CUBES: true,
    FETCH_COMBINATION: true,
  };
  multisearchUsage = MULTISEARCH_USAGE.ADVANCED_SEARCH;
  entityTid = null; // entity tid of the current object used in navigator component

  constructor(http, searchStore) {
    this.http = http;
    this.searchStore = searchStore;
    this.form = new MultiSearchFormStore(this.query);
    this.multisearchUsage = MULTISEARCH_USAGE.ADVANCED_SEARCH;
  }

  @action
  loadMaintenanceAgencies = async () => {
    const fetchParams = {
      tableName: "MaintenanceAgency",
      pageSize: 100,
      queryOffset: 0,
      filter: {
        sort: [],
        type: "MaintenanceAgency",
        bid: null,
      },
    };
    try {
      const json = await this.http.fetchContentManagementTableData(fetchParams);
      if (!json.error) {
        runInAction(() => {
          const maintenanceAgenciesSorted = json.results.sort((a, b) =>
            a.maintenanceAgencyId.localeCompare(b.maintenanceAgencyId)
          );
          for (const ma of maintenanceAgenciesSorted) {
            this.maintenanceAgencies[ma.tid] = ma;
          }

          // Set ECB as default MA
          const ecb = maintenanceAgenciesSorted.find(
            (ma) => ma.maintenanceAgencyId === "ECB"
          );
          if (ecb) {
            this.setMaintenanceAgencyAndLoadData([ecb.tid]);
          }
        });
      }
    } catch (error) {
      console.error("Error downloading maintenance agencies", error);
    }
  };

  @action
  setMaintenanceAgencyAndLoadData = (values) => {
    this.form.resetForm();
    this.form.resetAllowedFilters();
    this.selectedMaintenanceAgency = values;
    this.similarityThresholdPercentage =
      this.getDefaultSimilarityThresholdPercentage();
    this.query();
  };

  @action
  query = async () => {
    if (this.selectedMaintenanceAgency.length === 0) return;

    let validOn = moment(new Date()).format("YYYY-MM-DD"); // use today

    if (!this.isSimilarityThresholdPercentageValid()) {
      this.similarityThresholdPercentage =
        this.getDefaultSimilarityThresholdPercentage();
    }

    const queryParams = this.form.queryParams;
    queryParams.similarityThresholdPercentage =
      this.similarityThresholdPercentage;

    const configurationsForTypes = {};
    if (this.typesConfiguration.FETCH_CUBES) {
      configurationsForTypes.CUBES = queryParams;
    }
    if (this.typesConfiguration.FETCH_COMBINATION) {
      configurationsForTypes.COMBINATIONS = queryParams;
    }

    const params = (maintenanceAgencyTid) => ({
      maintenanceAgencyTid,
      validOn,
      cubeTypes: this.searchStore.getSelectedCubeTypes,
      configurationsForTypes: configurationsForTypes,
      multisearchUsage: this.multisearchUsage,
    });

    if (this.controllerForMultiSearchQuery && this.loadingQuery) {
      this.controllerForMultiSearchQuery.abort();
    }

    const controller = new window.AbortController();
    const signal = controller.signal;
    this.controllerForMultiSearchQuery = controller;

    try {
      this.loadingQuery = true;
      this.combinedResults = {
        CUBES: [],
        COMBINATIONS: [],
        combinedAllowedFilters: observable.array(),
      };
      const results = await Promise.all(
        this.selectedMaintenanceAgency.map((agencyTid) =>
          this.http.queryMultiSearch(params(agencyTid), signal)
        )
      );
      const uniqueFilters = new Map();
      results.forEach((json) => {
        if (!json.error && json.errorMessage == null) {
          this.combinedResults.CUBES.push(
            ...(json.resultsByType.CUBES?.results || [])
          );
          this.combinedResults.COMBINATIONS.push(
            ...(json.resultsByType.COMBINATIONS?.results || [])
          );

          if (json.combinedAllowedFilters) {
            json.combinedAllowedFilters.forEach((filter) => {
              if (!uniqueFilters.has(filter.variable?.tid)) {
                uniqueFilters.set(filter.variable.tid, filter);
              }
            });
          }
        }
      });
      this.combinedResults.combinedAllowedFilters.push(
        ...Array.from(uniqueFilters.values())
      );

      runInAction(() => {
        if (this.multisearchUsage === MULTISEARCH_USAGE.ADVANCED_SEARCH) {
          this.form.processAllowedFilters(
            this.combinedResults.combinedAllowedFilters
          );
        }
        this.cubesResults.replace(
          this.filterResults(this.combinedResults.CUBES)
        );
        this.combinationsResults.replace(
          this.filterResults(this.combinedResults.COMBINATIONS)
        );
        this.loadingQuery = false;
      });
    } catch (error) {
      runInAction(() => {
        this.loadingQuery = false;
      });
      if (error.name === "AbortError") {
        console.log("Combinations request aborted");
      } else {
        console.error("Error getting result.", error);
      }
    }
  };

  filterResults = (results) => {
    return this.multisearchUsage === MULTISEARCH_USAGE.NAVIGATOR_COMPONENT &&
      results?.length > 0
      ? results.filter((res) => res?.tid !== this.entityTid)
      : results;
  };

  @computed
  get allCubesResults() {
    const allCubesResults = this.cubesResults ? this.cubesResults : [];
    return allCubesResults.sort((a, b) => {
      // DESC
      if (a.matchPercentage < b.matchPercentage) return 1;
      if (a.matchPercentage > b.matchPercentage) return -1;

      // ASC
      if (a.code < b.code) return -1;
      if (a.code > b.code) return 1;

      // ASC
      const aName = a.name || "";
      const bName = b.name || "";
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return 0;
    });
  }

  @computed
  get frameworksDataModelsCubesTree() {
    const frameworksDataModelsCubesTree = {};
    for (const cube of this.allCubesResults) {
      const framework = cube.properties.FRAMEWORK || { name: "", code: "" };
      if (!frameworksDataModelsCubesTree[framework.code]) {
        frameworksDataModelsCubesTree[framework.code] = {
          framework,
          dataModels: {},
          individualCubes: [],
        };
      }

      const frameworkMap = frameworksDataModelsCubesTree[framework.code];

      // DATA MODEL LEVEL
      if (cube.properties.DATA_MODELS && cube.properties.DATA_MODELS.length) {
        for (const dataModel of cube.properties.DATA_MODELS) {
          if (!frameworkMap.dataModels[dataModel.tid]) {
            frameworkMap.dataModels[dataModel.tid] = {
              dataModel,
              cubes: [],
            };
          }
          frameworkMap.dataModels[dataModel.tid].cubes.push(cube);
        }
      } else {
        frameworkMap.individualCubes.push(cube);
      }
    }
    return frameworksDataModelsCubesTree;
  }

  @computed
  get dataModelsWithCubes() {
    const dataModelsWithCubes = [];
    for (const frameworkMap of Object.values(
      this.frameworksDataModelsCubesTree
    )) {
      for (const dataModelMap of Object.values(frameworkMap.dataModels)) {
        if (dataModelMap.cubes.length > 0) {
          dataModelsWithCubes.push({
            ...dataModelMap.dataModel,
            cubes: dataModelMap.cubes,
            framework: frameworkMap.framework,
          });
        }
      }
      // Individual cubes
      if (frameworkMap.individualCubes.length > 0) {
        dataModelsWithCubes.push({
          cubes: frameworkMap.individualCubes,
          framework: frameworkMap.framework,
        });
      }
    }
    return dataModelsWithCubes;
  }

  @computed
  get getSimilarityThresholdPercentage() {
    return this.similarityThresholdPercentage;
  }

  @action
  setSimilarityThresholdPercentage = (value) => {
    const hasValueChanged = value !== this.similarityThresholdPercentage;
    this.similarityThresholdPercentage = value;

    if (hasValueChanged) {
      this.similarityThresholdChangedDebounced();
    }
  };

  similarityThresholdChangedDebounced = debounce(this.query, 500);

  isSimilarityThresholdPercentageValid = () => {
    return !isNaN(this.similarityThresholdPercentage);
  };

  setTypesConfiguration(type, shouldBeFetched) {
    this.typesConfiguration[type] = !!shouldBeFetched;
  }

  setMultisearchUsage(usage) {
    this.multisearchUsage = usage;
  }

  @action
  setSelectedMaintenanceAgency = (selectedMA) => {
    this.selectedMaintenanceAgency = [selectedMA];
  };

  getDefaultSimilarityThresholdPercentage = () => {
    return EnvironmentUtils.isBirdApp() ? 0 : 100;
  };

  @action
  setEntityTid = (entityTid) => {
    this.entityTid = entityTid;
  };
}
